import React, { useState, useEffect, useRef } from 'react';
import './AnimatedSectionTitle.css';

interface AnimatedSectionTitleProps {
  title: string;
  className?: string;
}

const AnimatedSectionTitle: React.FC<AnimatedSectionTitleProps> = ({ title, className }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isTypingComplete, setIsTypingComplete] = useState(false);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const currentTitle = useRef(title); // Use ref to keep title stable across renders

  const typingSpeed = 80;
  const pauseDuration = 200;

  useEffect(() => {
    currentTitle.current = title; // Update ref if title prop changes
  }, [title]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isTypingComplete) {
          let charIndex = 0;
          const type = () => {
            if (charIndex < currentTitle.current.length) {
              setDisplayedText(currentTitle.current.substring(0, charIndex + 1));
              charIndex++;
              setTimeout(type, typingSpeed);
            } else {
              setIsTypingComplete(true);
              observer.disconnect(); // Stop observing once animation is complete
            }
          };
          setTimeout(type, pauseDuration); // Initial pause before typing starts
        }
      },
      { threshold: 0.5 }
    );

    if (titleRef.current) {
      observer.observe(titleRef.current);
    }

    return () => {
      if (titleRef.current) {
        observer.unobserve(titleRef.current);
      }
    };
  }, [isTypingComplete]); // Only re-run effect if typing completion state changes

  return (
    <h2 ref={titleRef} className={`${className} typing-animation`}>
      {displayedText}
    </h2>
  );
};

export default AnimatedSectionTitle;